<template>
  <div style="display: flex; flex-direction: column">
    <div class="inline-flex q-mb-xs">
      <div class="move-row">
        <span
          v-html="moveTypeName(el.MovementType)"
        />
        <app-tooltip
          v-if="!hideInfoBtn && el.Id > 0"
          :text="$t('info')"
          :style="{ height: '18px' }"
        >
          <template #activator>
            <app-icon
              class="move-row__info q-mr-xs"
              name="info-circle-outline"
              width="18px"
              color="#979797"
              hover-color="#499259"
              cursor="pointer"
              @click="openInfoDialog"
            />
          </template>
        </app-tooltip>
        <div :class="['move-row']">
          <span>
            <span
              v-for="(part, pidx) in displayLayout.head"
              :key="'mv_part_' + pidx"
              class="q-mr-xs"
              v-html="part"
            ></span>
            <span
              v-if="showPositionsToggle"
              class="move-row__tail"
            >
              <span
                v-if="displayLayout.tail"
                v-html="displayLayout.tail"
              ></span>
              <button
                type="button"
                class="positions-toggle"
                @click.stop="$emit('toggle-positions')"
              >
                <span class="positions-toggle__label">{{ positionsCountLabel }}</span>
                <app-icon
                  v-if="positionsExpanded"
                  class="positions-toggle__chevron"
                  name="chevron-down"
                  width="10px"
                  color="#979797"
                />
              </button>
            </span>
          </span>
        </div>
      </div>
      <app-tooltip
        v-if="!hideRemoveBtn"
        :text="$t('remove')"
        :style="{ height: '16px' }"
      >
        <template #activator>
          <app-icon
            class="ml-1 move-row__remove"
            name="close"
            width="16px"
            color="#979797"
            hover-color="#616161"
            cursor="pointer"
            @click.stop="detach"
          />
        </template>
      </app-tooltip>
    </div>
    <div v-if="showPositions">
      <div
        v-for="(pos, idx) in el.Items"
        class="row no-wrap items-baseline"
        :key="'item_pos_' + idx"
      >
        {{ idx + 1 }})&nbsp;
        <!-- <parameter-inline-display
          :id="'ds_mr_' + pos.Id"
          :editable="false"
          :is-table="false"
          :model-value="getPositionFromNameInstance(pos)"
        /> -->
        <position-row
          :id="pos.Id"
          :el="getPositionFromNameInstance(pos)"
          :hide-info-btn="true"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useQuasar } from "quasar";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { V2ParameterValueType } from "../../../../models/etc/parameter-types";
import {
  ParameterInstanceContainerType,
  V2FieldValueDisplayType,
  V2RecordAxisType,
  V2StorageItemMovementType,
} from "../../../../models/etc/types";
import { mapStorageItemInstanceToParameterInstance } from "../../../../models/helpers/parameter-helpers";
import {
  StorageItemInMovement,
  StorageItemMovementInFinances,
} from "../../../../models/interfaces/finances/finance-models";
import {
  ParameterInstance,
  ParameterInstanceContainer,
} from "../../../../models/interfaces/parameters/parameter-models";
import MoveInfoDialog from "./MoveInfoDialog.vue";
import PositionRow from "./PositionRow.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

// const $q = useQuasar()

@Component({
  name: "MovementRow",
  components: { PositionRow, MoveInfoDialog, AppIcon, AppTooltip },
  emits: ["show-info", "detach", "toggle-positions"],
})
export default class MovementRow extends Vue {
  // === PROPS ===
  @Prop({ required: true }) readonly el!: StorageItemMovementInFinances;
  @Prop({ type: Boolean, default: false }) readonly hideInfoBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly hideRemoveBtn!: boolean;
  @Prop({ type: Boolean, default: true }) readonly showPositions!: boolean;
  // Кнопка сворачивания списка позиций: сам список рисует вызывающий, тут только переключатель
  @Prop({ type: Boolean, default: false }) readonly showPositionsToggle!: boolean;
  @Prop({ type: Boolean, default: false }) readonly positionsExpanded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readOnly!: boolean;

  private $q = useQuasar();

  get positionsCountLabel(): string {
    const count = this.el.Items?.length ?? 0;
    const locale = (this.$i18n?.locale ?? "ru") as string;
    if (locale === "ru") {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod100 >= 11 && mod100 <= 19) {
        return `${count} ${this.$t("positions_count_many")}`;
      }
      if (mod10 === 1) {
        return `${count} ${this.$t("positions_count_one")}`;
      }
      if (mod10 >= 2 && mod10 <= 4) {
        return `${count} ${this.$t("positions_count_few")}`;
      }
      return `${count} ${this.$t("positions_count_many")}`;
    }
    return count === 1
      ? `${count} ${this.$t("positions_count_one")}`
      : `${count} ${this.$t("positions_count_many")}`;
  }

  // showMoveInfoMode = false;

  // === Computed: части строки, каждая рисуется своим span ===
  // Части отдаём списком, а не одной html-строкой: кнопка позиций должна стоять
  // внутри того же span, что и текст, чтобы быть в потоке строки как отдельное слово
  get displayParts(): string[] {
    // if (this.el.isLoading) return ['<span class="text-italic">Загрузка...</span>'];

    const parts: string[] = [];

    // Артикул
    parts.push(
      `${this.el.VendorCode ? this.el.VendorCode : this.$t('being_created')}`
    );

    // Дата
    parts.push(`${this.$t('date_from_prefix')} ${this.formatDate(this.el.Date)}`);

    // Склад откуда
    if (
      this.el.MovementType === V2StorageItemMovementType.Out ||
      this.el.MovementType === V2StorageItemMovementType.Inner
    ) {
      parts.push(`${this.el.SourceStorage?.Name}`);
    }
    // ->
    if (this.el.MovementType === V2StorageItemMovementType.Inner) {
      parts.push(`-&gt;`);
    }
    // Склад откуда
    if (
      this.el.MovementType === V2StorageItemMovementType.In ||
      this.el.MovementType === V2StorageItemMovementType.Inner
    ) {
      parts.push(`${this.el.ReceiverStorage?.Name}`);
    }

    // Комментарий
    if (this.el.Details) {
      parts.push(
        `<span class="q-mr-xs">${this.highlightText(this.el.Details)}</span>`
      );
    }

    return parts;
  }

  // Последнее слово текста и кнопку держим в одной неразрывной связке, иначе перенос
  // случается перед кнопкой и она остаётся на строке одна, хотя текст ещё не переносился.
  get displayLayout(): { head: string[]; tail: string } {
    const parts = this.displayParts;
    if (!this.showPositionsToggle || parts.length === 0) {
      return { head: parts, tail: "" };
    }

    const head = parts.slice(0, -1);
    const last = parts[parts.length - 1].trimEnd();
    const cut = last.lastIndexOf(" ");
    if (cut === -1) return { head, tail: last };

    return { head: [...head, last.slice(0, cut)], tail: last.slice(cut + 1) };
  }

  openInfoDialog() {
    this.$q.dialog({
      component: MoveInfoDialog,
      componentProps: {
        existingMovementId: this.el.Id,
        createType: this.el.MovementType,
        readOnly: this.readOnly
      }
    })
    .onOk(() => {
      console.log('Диалог успешно закрыт (пользователь сохранил или просто закрыл)')
      // Здесь можно обновить список, перезагрузить данные и т.д.
    })
    .onCancel(() => {
      console.log('Отмена (если добавишь onDialogCancel)')
    })
    .onDismiss(() => {
      console.log('Диалог закрыт любым способом')
    })
  }

  getPositionFromNameInstance(pos: StorageItemInMovement) {
    const pos_copy = mapStorageItemInstanceToParameterInstance(
      pos.NameParameters
    );
    const vcode_cell = {
      Type: ParameterInstanceContainerType.SingleInstance,
      Instance: {
        TemplateId: 999,
        Unremovable: true,
        ValueType: V2ParameterValueType.String,
        StringValue: pos.VendorCode,
        NumberValue: null,
        DateValue: null,
        BoolValue: null,
        ValueSet: null,
        SelectedValue: null,
        ValueDisplayType: V2FieldValueDisplayType.Inline,
        Name: this.$t('sku'),
        Id: 0,
        Round: 2,
        RecordAxisType: V2RecordAxisType.Horizontal,
        RecordFields: [],
      } as ParameterInstance,
      ListTitle: null,
    } as ParameterInstanceContainer;

    return [vcode_cell, ...pos_copy];
  }

  // movement type icon span
  moveTypeName(arg: number): string {
    switch (arg) {
      // case MovementTypeFilterFlagsDto.Both:
      // return this.$t('both_label')
      case V2StorageItemMovementType.Out:
        return '<span class="no-break movement-element movement-element--expense"/>';
      case V2StorageItemMovementType.In:
        return '<span class="no-break movement-element movement-element--income"/>';
      case V2StorageItemMovementType.Inner:
        return `<span class="no-break movement-element movement-element--transfer"/>`;
      default:
        return this.$t("filter_empty");
    }
  }

  // === Методы ===
  highlightText(text: string): string {
    // Здесь должна быть логика фильтрации (например, из глобального поиска)
    // Пока — просто экранируем
    return text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}.${d.getFullYear().toString().slice(-2)}`;
  }

  // === События ===
  showInfo() {
    this.$emit("show-info", { Id: this.el.Id, Type: this.el.MovementType });
  }

  detach() {
    this.$emit("detach", { Id: this.el.Id, Type: this.el.MovementType });
  }
}
</script>

<style lang="scss" scoped>
.move-row {
  display: inline;
}

.move-row :deep(.green-text) {
  color: $primary;
  font-weight: 500;
}

.move-row :deep(.text-medium) {
  font-weight: 500;
}

.move-row :deep(.text-span) {
  color: $text-color;
  font-style: italic;
}

.inline-flex {
  display: inline-flex;
  align-items: flex-start;
}

.move-row__tail {
  white-space: nowrap;
}

.move-row__info {
  margin-top: -2px;
}

.move-row__remove {
  margin-top: 1px;
}

.positions-toggle {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  gap: 2px;
  height: 16px;
  margin-left: 4px;
  padding: 2px 4px;
  white-space: nowrap;
  border: 0;
  border-radius: 8px;
  background: #F2F2F2;
  cursor: pointer;
}

.positions-toggle:hover {
  background: #E8E8E8;
}

.positions-toggle__label {
  color: #979797;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 9px;
}

.positions-toggle__chevron {
  transform: rotate(180deg);
}
</style>
